"use client"

import { useState } from "react"
import { X, Check, Pencil, RefreshCw, SkipForward, Sparkles, FileText, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import type { LogosReasoning } from "@/lib/types/logos"

interface ReviewAIDraftModalProps {
  isOpen: boolean
  onClose: () => void
  fieldLabel: string
  draftContent: string
  confidence: number
  reasoning?: LogosReasoning
  sourceSections?: string[]
  onApprove: () => void
  onEdit: (editedContent: string) => void
  onRegenerate: () => void
  onSkip: () => void
}

export function ReviewAIDraftModal({
  isOpen,
  onClose,
  fieldLabel,
  draftContent,
  confidence,
  reasoning,
  sourceSections = [],
  onApprove,
  onEdit,
  onRegenerate,
  onSkip,
}: ReviewAIDraftModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState(draftContent)
  const [showReasoning, setShowReasoning] = useState(false)

  if (!isOpen) return null

  const handleApprove = () => {
    if (isEditing) {
      onEdit(editedContent)
    } else {
      onApprove()
    }
    onClose()
  }

  const handleRegenerate = () => {
    onRegenerate()
    // Modal stays open, content will update
  }

  const handleSkip = () => {
    onSkip()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-[var(--surface)] border border-[var(--border-light)] rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-[var(--border-light)]">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--ai-violet)] shrink-0" />
            <h2 className="text-base sm:text-title text-foreground truncate">{fieldLabel}</h2>
            <span className="text-caption text-[var(--text-tertiary)] hidden sm:inline">Review Required</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--surface-hover)] motion-fast shrink-0">
            <X className="h-5 w-5 text-[var(--text-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-y-auto max-h-[50vh] sm:max-h-[60vh]">
          <p className="text-body text-[var(--text-secondary)]">Based on our conversation, I drafted this:</p>

          {/* Draft content */}
          <div className="border border-[var(--border-light)] rounded-lg p-4 bg-[var(--surface-inset)]">
            {isEditing ? (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="min-h-[120px] bg-transparent border-none p-0 focus-visible:ring-0 resize-none text-body text-foreground"
                autoFocus
              />
            ) : (
              <p className="text-body text-foreground whitespace-pre-wrap">{draftContent}</p>
            )}
          </div>

          {/* Confidence & Sources */}
          <div className="flex items-center gap-4 text-caption">
            <div className="flex items-center gap-1.5">
              <span className="text-[var(--text-tertiary)]">AI Confidence:</span>
              <span
                className={cn(
                  "font-medium",
                  confidence >= 0.8
                    ? "text-[var(--success)]"
                    : confidence >= 0.6
                      ? "text-[var(--warning)]"
                      : "text-[var(--danger)]",
                )}
              >
                {Math.round(confidence * 100)}%
              </span>
            </div>
            {sourceSections.length > 0 && (
              <div className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
                <span className="text-[var(--text-tertiary)]">Based on: {sourceSections.join(", ")}</span>
              </div>
            )}
          </div>

          {/* Reasoning toggle */}
          {reasoning && (
            <div>
              <button
                onClick={() => setShowReasoning(!showReasoning)}
                className="text-caption text-[var(--cta)] hover:underline"
              >
                {showReasoning ? "Hide reasoning" : "Show how I arrived at this"}
              </button>

              {showReasoning && (
                <div className="mt-3 p-3 border border-[var(--border-light)] rounded-lg bg-[var(--surface-inset)] space-y-3">
                  <div>
                    <p className="text-caption font-medium text-[var(--text-secondary)] mb-1">Reasoning:</p>
                    {reasoning.reasoning_steps.map((step, i) => (
                      <p key={i} className="text-caption text-[var(--text-tertiary)]">
                        {i + 1}. {step.thought}
                      </p>
                    ))}
                  </div>

                  {reasoning.assumptions.length > 0 && (
                    <div>
                      <p className="text-caption font-medium text-[var(--text-secondary)] mb-1">Assumptions:</p>
                      {reasoning.assumptions.map((a, i) => (
                        <p key={i} className="text-caption text-[var(--text-tertiary)]">
                          • {a}
                        </p>
                      ))}
                    </div>
                  )}

                  {reasoning.alternative_conclusions.length > 0 && (
                    <div>
                      <p className="text-caption font-medium text-[var(--text-secondary)] mb-1">
                        Alternatives considered:
                      </p>
                      {reasoning.alternative_conclusions.map((alt, i) => (
                        <p key={i} className="text-caption text-[var(--text-tertiary)]">
                          • {alt.conclusion} — {alt.why_not_chosen}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Question */}
          <div className="flex items-start gap-2 p-3 border border-[var(--info)] rounded-lg bg-[var(--info)]/5">
            <AlertCircle className="h-4 w-4 text-[var(--info)] mt-0.5 shrink-0" />
            <p className="text-body text-foreground">Does this accurately capture your {fieldLabel.toLowerCase()}?</p>
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-[var(--border-light)] flex flex-col sm:flex-row flex-wrap gap-2">
          <Button
            onClick={handleApprove}
            className="w-full sm:flex-1 min-h-[48px] sm:min-h-[44px] gap-2 bg-[var(--cta)] text-white hover:bg-[var(--cta-hover)]"
          >
            <Check className="h-4 w-4" />
            {isEditing ? "Save Edits" : "Approve"}
          </Button>

          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(!isEditing)
                if (!isEditing) setEditedContent(draftContent)
              }}
              className="flex-1 sm:flex-none min-h-[48px] sm:min-h-[44px] gap-2 border-[var(--border-light)]"
            >
              <Pencil className="h-4 w-4" />
              <span className="sm:inline">{isEditing ? "Cancel" : "Edit"}</span>
            </Button>

            <Button
              variant="outline"
              onClick={handleRegenerate}
              className="flex-1 sm:flex-none min-h-[48px] sm:min-h-[44px] gap-2 border-[var(--border-light)] bg-transparent"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Regenerate</span>
            </Button>

            <Button
              variant="ghost"
              onClick={handleSkip}
              className="min-h-[48px] sm:min-h-[44px] gap-2 text-[var(--text-tertiary)]"
            >
              <SkipForward className="h-4 w-4" />
              <span className="hidden sm:inline">Skip</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
